import type { IExperimentUsage } from '../../modal-pick-template/types';

import {
  PICK_PAGE_RESOURCE_QUERY_KEY,
  STOREFRONT_RESOURCE_QUERY_KEYS,
} from '~/constants/storefrontResource';
import { Box, Button, Filters, InlineStack, ResourceList } from '@shopify/polaris';
import { MODAL_EDIT_EXPERIMENTS_IDENTIFIER } from '~/modules/campaign/constants';
import { PLATFORM_PRODUCTS_QUERY_KEY } from '~/constants/platformProduct';
import { GModal, GPickResourceScrollable } from '~/presentation/common';
import { useQueryCampaignUrls } from '~/modules/campaign-url-redirect';
import { EditExperimentEmptyState } from './EditExperimentEmptyState';
import { useQueryCampaignPrices } from '~/modules/campaign-price';
import { EditExperimentItem } from './EditExperimentItem';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { RefreshIcon } from '@shopify/polaris-icons';
import { useAsyncWithErrorToast } from '~/hooks';
import { useTranslation } from 'react-i18next';
import { GTooltip } from '~/presentation/ui';

interface IProps {
  open: boolean;
  usages: IExperimentUsage[];
  onClose: () => void;
  onOpenRemoveConfirm: (usage: IExperimentUsage) => void;
}

/**
 * Every experiment holding one template through a record of its own — the product a price test runs
 * on, or the page a URL test sends traffic to. A template renders for a set of pages, and each of
 * those can be under test at once — too many for the template card to name, so they are listed here
 * with the same actions the card offers for a single one.
 */
export const ModalEditExperiments: React.FC<IProps> = ({
  open,
  usages,
  onClose,
  onOpenRemoveConfirm,
}) => {
  const { t } = useTranslation();
  const { handleRefetch: handleRefetchCampaignPrices, isFetching: isFetchingCampaignPrices } =
    useQueryCampaignPrices();
  const { handleRefetch: handleRefetchCampaignUrls, isFetching: isFetchingCampaignUrls } =
    useQueryCampaignUrls();
  const withErrorToast = useAsyncWithErrorToast();
  const queryClient = useQueryClient();

  const [queryValue, setQueryValue] = useState('');

  const isFetching = isFetchingCampaignPrices || isFetchingCampaignUrls;

  const filteredUsages = useMemo(() => {
    const keyword = queryValue.trim().toLowerCase();
    if (!keyword) return usages;

    return usages.filter(({ campaignName, resourceName }) =>
      [campaignName, resourceName].some((value) => value?.toLowerCase().includes(keyword)),
    );
  }, [usages, queryValue]);

  const handleClearQuery = useCallback(() => setQueryValue(''), []);

  const handleRefresh = useCallback(
    () =>
      withErrorToast(
        async () => {
          // The template suffix deciding whether a row belongs in this list at all — and a price
          // row's product name — come from the record behind the page, not from the campaign
          // queries, and nothing else invalidates those, so a refresh without this would never see
          // either change.
          await Promise.all([
            handleRefetchCampaignPrices(),
            handleRefetchCampaignUrls(),
            queryClient.invalidateQueries({ queryKey: [PLATFORM_PRODUCTS_QUERY_KEY] }),
            ...[
              ...STOREFRONT_RESOURCE_QUERY_KEYS,
              ...Object.values(PICK_PAGE_RESOURCE_QUERY_KEY),
            ].map((queryKey) => queryClient.invalidateQueries({ queryKey: [queryKey] })),
          ]);
        },
        {
          source: 'ModalEditExperiments.handleRefresh',
          tags: { experiment: 'Experiment' },
        },
      ),
    [withErrorToast, handleRefetchCampaignPrices, handleRefetchCampaignUrls, queryClient],
  );

  return (
    <>
      <GModal size="large" noScroll open={open} title={t('Edit experiments')} onClose={onClose}>
        {/* Rows are listed, not picked, so they drop the pointer cursor a ResourceItem always sets.
            The identifier class also carries the modal height and the overlay-button rule in
            AssetTemplate.scss. */}
        <div
          className={`${MODAL_EDIT_EXPERIMENTS_IDENTIFIER} flex min-h-0 flex-1 flex-col [&_.Polaris-ResourceItem]:cursor-default`}
        >
          {/* Shares the template picker's filter styles, which restore the search icon on mobile. */}
          <div className="gx-template-filters">
            <Filters
              queryValue={queryValue}
              filters={[]}
              queryPlaceholder={t('Searching experiments')}
              onQueryChange={setQueryValue}
              onQueryClear={handleClearQuery}
              onClearAll={handleClearQuery}
            >
              <Box minHeight="100%">
                <InlineStack gap="200">
                  <GTooltip content={t('Refresh to update')}>
                    <Button icon={RefreshIcon} disabled={isFetching} onClick={handleRefresh} />
                  </GTooltip>
                </InlineStack>
              </Box>
            </Filters>
          </div>

          {/* Fills whatever the pinned modal height leaves, so the list grows instead of the modal.
              `fillPinnedDialog` flex-fills that remainder -- `default` would reserve calc() space for
              a footer this modal doesn't have, leaving a gap between the list and the dialog's pinned
              height. Only valid because AssetTemplate.scss pins this dialog's height and carries the
              flex chain down to here -- see the variant's own comment in constants.ts. */}
          <GPickResourceScrollable variant="fillPinnedDialog" className="Polaris-GTemplate">
            <ResourceList
              resourceName={{ singular: t('experiment'), plural: t('experiments') }}
              items={filteredUsages}
              showHeader
              emptyState={
                <EditExperimentEmptyState
                  hasKeyword={!!queryValue.trim()}
                  isRefreshing={isFetching}
                  onRefresh={handleRefresh}
                />
              }
              renderItem={(usage) => (
                <EditExperimentItem
                  key={usage.campaignId}
                  usage={usage}
                  onOpenRemoveConfirm={onOpenRemoveConfirm}
                />
              )}
            />
          </GPickResourceScrollable>
        </div>
      </GModal>
    </>
  );
};
